import { useState } from "react";
import { Trash2 } from "lucide-react";
import type { MedicalCondition, RatingDraft, RatingWithEffectiveDebits } from "../../types";
import { getApi } from "../../api";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { confirmAction, notify } from "../../store/uiStore";
import { reportError } from "../../services/eventBus";
import { Button } from "../Button";
import { Card } from "../Card";
import { PageHeader } from "../PageHeader";
import { TagPill } from "../Pills";
import { ErrorState, Skeleton } from "../ScreenStates";
import { TextArea, TextInput, ToggleInput } from "../fields";
import { RatingRowEditor } from "./RatingRowEditor";
import { NewRatingBar } from "./NewRatingBar";
import { UnderwritingRuleEditor } from "./UnderwritingRuleEditor";

interface ConditionEditorProps {
  condition: MedicalCondition;
  /** Rating to highlight and scroll to when arriving from the customizations list. */
  focusRatingId?: number | null;
  onConditionChanged: (condition: MedicalCondition | null) => void;
  onBack: () => void;
  children?: never;
}

const HEAD = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500";

/**
 * Everything about one condition: the page header carries name, category,
 * origin, the active flag, and delete; the card holds editable details (custom
 * only) and one ratings table where each row carries text, base debits,
 * multiplier or override, live effective debits, notes, and active, with an
 * add-a-rating bar underneath.
 */
export function ConditionEditor({ condition, focusRatingId = null, onConditionChanged, onBack }: ConditionEditorProps) {
  const [version, setVersion] = useState(0);
  const ratings = useAsyncResource(() => getApi().getConditionRatings(condition.id, true), `${condition.id}-${version}`, "ConditionEditor.ratings");
  const [name, setName] = useState(condition.name);
  const [description, setDescription] = useState(condition.description ?? "");
  const [notes, setNotes] = useState(condition.notes ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = name !== condition.name || description !== (condition.description ?? "") || notes !== (condition.notes ?? "");

  const reloadRatings = async () => {
    setVersion((current) => current + 1);
  };

  const handleSaveDetails = async () => {
    setIsSaving(true);
    try {
      const saved = await getApi().updateCondition(condition.id, { name: name.trim(), description: description.trim() || null, notes: notes.trim() || null });
      onConditionChanged(saved);
      notify(`Saved ${saved.name}.`, "success");
    } catch (error) {
      reportError(error, "ConditionEditor.saveDetails");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (isActive: boolean) => {
    try {
      const saved = await getApi().updateCondition(condition.id, { isActive });
      onConditionChanged(saved);
      notify(`${saved.name} is now ${isActive ? "active" : "inactive"} for underwriters and Skynet.`, "success");
    } catch (error) {
      reportError(error, "ConditionEditor.toggleActive");
    }
  };

  const handleDeleteCondition = async () => {
    const confirmed = await confirmAction({ title: "Delete condition", message: `Delete ${condition.name} and all of its ratings? Groups that already carry these ratings keep their snapshots.`, confirmLabel: "Delete", variant: "danger" });
    if (!confirmed) return;
    try {
      await getApi().deleteCondition(condition.id);
      notify(`Deleted ${condition.name}.`, "success");
      onConditionChanged(null);
    } catch (error) {
      reportError(error, "ConditionEditor.deleteCondition");
    }
  };

  const handleAddRating = async (draft: RatingDraft): Promise<boolean> => {
    try {
      const rating = await getApi().createRating(condition.id, draft);
      notify(`Added "${rating.ratingText}" to ${condition.name}. Skynet sees it immediately.`, "success");
      await reloadRatings();
      return true;
    } catch (error) {
      reportError(error, "ConditionEditor.addRating");
      return false;
    }
  };

  const handleDeleteRating = async (rating: RatingWithEffectiveDebits) => {
    const confirmed = await confirmAction({ title: "Delete rating", message: `Delete "${rating.ratingText}" from ${condition.name}?`, confirmLabel: "Delete", variant: "danger" });
    if (!confirmed) return;
    try {
      await getApi().deleteRating(rating.ratingId);
      notify(`Deleted ${rating.ratingText}.`, "success");
      await reloadRatings();
    } catch (error) {
      reportError(error, "ConditionEditor.deleteRating");
    }
  };

  const activeCount = ratings.data?.filter((rating) => rating.isActive).length ?? 0;
  const modifiedCount = ratings.data?.filter((rating) => rating.modification !== null).length ?? 0;

  return (
    <>
      <PageHeader
        title={condition.name}
        badges={
          <>
            <TagPill label={condition.category} />
            {condition.isCustom ? <TagPill label="Custom" tone="green" /> : <TagPill label="MUGS" tone="navy" />}
            {!condition.isActive && <TagPill label="Inactive" tone="warning" />}
          </>
        }
        subtitle={condition.isCustom ? "Custom condition. Edit its details and ratings below; changes reach underwriters and Skynet immediately." : "Condition from the MUGS manual. Its text is read-only; adjust what each rating is worth, add custom ratings, or deactivate it."}
        actions={
          <>
            <ToggleInput id="condition-active" label={condition.isActive ? "Active" : "Inactive"} checked={condition.isActive} onChange={(value) => void handleToggleActive(value)} />
            {condition.isCustom && (
              <Button variant="danger" size="sm" icon={Trash2} onClick={() => void handleDeleteCondition()}>
                Delete condition
              </Button>
            )}
            <Button variant="secondary" onClick={onBack}>
              Back to customizations
            </Button>
          </>
        }
      />
      <Card ariaLabel={condition.name}>
        <div className="space-y-5">
          {condition.isCustom ? (
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
              <TextInput id="condition-name" label="Name" value={name} onChange={setName} required />
              <TextArea id="condition-description" label="Description" value={description} onChange={setDescription} rows={1} />
              <TextArea id="condition-notes" label="Notes" value={notes} onChange={setNotes} rows={1} />
              <Button onClick={() => void handleSaveDetails()} disabled={!isDirty || name.trim() === ""} isLoading={isSaving}>
                Save details
              </Button>
            </div>
          ) : (
            condition.description && <p className="max-w-4xl text-sm leading-relaxed text-gray-600">{condition.description}</p>
          )}

          <UnderwritingRuleEditor conditionId={condition.id} conditionName={condition.name} />

          <div>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 className="text-[15px] font-semibold text-brand-navy">Ratings</h2>
              {ratings.data && (
                <span className="text-xs text-gray-500">
                  {ratings.data.length} rating{ratings.data.length === 1 ? "" : "s"}, {activeCount} active{modifiedCount > 0 ? `, ${modifiedCount} with a debit modification` : ""}
                </span>
              )}
            </div>
            {ratings.state.kind === "error" && <ErrorState message={ratings.state.message} onRetry={ratings.reload} />}
            {ratings.state.kind === "loading" && !ratings.data && (
              <div className="space-y-2" role="status" aria-label="Loading ratings">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}
            {ratings.data && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-[13px]">
                  <caption className="sr-only">Ratings for {condition.name}</caption>
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th scope="col" className={HEAD}>Rating</th>
                      <th scope="col" className={`${HEAD} w-32`}>Time</th>
                      <th scope="col" className={`${HEAD} w-20 text-right`}>Base</th>
                      <th scope="col" className={`${HEAD} w-24`} title="Scales the base debits for every future assignment">Multiplier</th>
                      <th scope="col" className={`${HEAD} w-28`} title="Replaces the base debits outright. One of Multiplier or Override, not both.">Override</th>
                      <th scope="col" className={`${HEAD} w-24 text-right`}>Effective</th>
                      <th scope="col" className={`${HEAD} w-64`}>Notes</th>
                      <th scope="col" className={`${HEAD} w-16 text-center`}>Active</th>
                      <th scope="col" className={`${HEAD} w-32 text-right`}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ratings.data.map((rating) => (
                      <RatingRowEditor key={`${rating.ratingId}-${version}`} rating={rating} isFocused={rating.ratingId === focusRatingId} onChanged={reloadRatings} onDelete={() => void handleDeleteRating(rating)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {ratings.data && <NewRatingBar conditionName={condition.name} onAdd={handleAddRating} />}
            <p className="mt-3 text-xs text-gray-500">A multiplier or an override changes future assignments only. MUGS ratings can be modified or deactivated, not edited or deleted.</p>
          </div>
        </div>
      </Card>
    </>
  );
}
